import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Box,
  CardMedia,
} from '@mui/material';
import { Course } from '@e-learning/shared/src/store/types';
import SchoolIcon from '@mui/icons-material/School';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BookIcon from '@mui/icons-material/Book';

interface CourseCardProps {
  course: Course;
  onClick?: () => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onClick }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      navigate(`/courses/${course.id}`);
    }
  };

  // Get category color gradient
  const getCategoryGradient = (category: string) => {
    const gradients: Record<string, string> = {
      JavaScript: 'linear-gradient(135deg, rgba(247, 223, 30, 0.3) 0%, rgba(255, 193, 7, 0.3) 100%)',
      TypeScript: 'linear-gradient(135deg, rgba(49, 120, 198, 0.3) 0%, rgba(0, 122, 204, 0.3) 100%)',
      Python: 'linear-gradient(135deg, rgba(48, 135, 188, 0.3) 0%, rgba(255, 193, 7, 0.3) 100%)',
      'C#': 'linear-gradient(135deg, rgba(128, 0, 128, 0.3) 0%, rgba(0, 120, 215, 0.3) 100%)',
      PHP: 'linear-gradient(135deg, rgba(119, 123, 179, 0.3) 0%, rgba(77, 77, 255, 0.3) 100%)',
      Java: 'linear-gradient(135deg, rgba(244, 67, 54, 0.3) 0%, rgba(255, 152, 0, 0.3) 100%)',
    };
    return gradients[category] || 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)';
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.1)',
        borderRadius: 3,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #6366f1 0%, #ec4899 50%, #8b5cf6 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        },
        '&:hover': {
          transform: 'translateY(-12px) scale(1.02)',
          borderColor: 'rgba(99, 102, 241, 0.4)',
          boxShadow: '0 24px 48px rgba(99, 102, 241, 0.25)',
          '&::before': {
            opacity: 1,
          },
          '& .course-header': {
            transform: 'scale(1.05)',
          },
          '& .course-icon': {
            transform: 'rotate(10deg) scale(1.1)',
          },
        },
      }}
      onClick={handleClick}
    >
      <Box
        className="course-header"
        sx={{
          height: 180,
          background: getCategoryGradient(course.category),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          transition: 'transform 0.4s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            animation: 'pulse 3s ease-in-out infinite',
          },
        }}
      >
        <SchoolIcon
          className="course-icon"
          sx={{
            fontSize: 80,
            color: 'primary.main',
            position: 'relative',
            zIndex: 1,
            filter: 'drop-shadow(0 4px 8px rgba(99, 102, 241, 0.3))',
            transition: 'transform 0.4s ease',
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Chip
            label={course.category}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
          {course.sections && course.sections.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <BookIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {course.sections.length} {course.sections.length === 1 ? 'Section' : 'Sections'}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: 2,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {course.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 3,
            flexGrow: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.6,
          }}
        >
          {course.description}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          endIcon={<ArrowForwardIcon />}
          onClick={handleClick}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            borderRadius: 2,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              transform: 'translateX(4px)',
              boxShadow: '0 8px 16px rgba(99, 102, 241, 0.4)',
            },
          }}
        >
          Explore Course
        </Button>
      </CardContent>
    </Card>
  );
};

export default CourseCard;

